import { runInventoryTransaction } from './inventoryTransactions';
import { supabase } from '@/lib/supabase';
import { fetchAllRows } from '@/lib/fetchAllRows';

const normalizeEntryMode = (document = {}, type) => {
  if (type !== 'entrada') return null;
  return document.entry_mode || 'normal';
};

export const generateDocumentNumber = async (type) => {
  try {
    // Obtener todos los números existentes de este tipo (paginado) y calcular el máximo sufijo numérico
    const PAGE_SIZE = 1000;
    let offset = 0;
    let lastNumber = 0;

    const extractNumericSuffix = (raw) => {
      if (!raw) return NaN;
      const str = String(raw);
      // Tomar la última secuencia de dígitos en la cadena (funciona para SAL-<ts>, sal-000001, 000001, etc.)
      const match = str.match(/(\d+)(?!.*\d)/);
      if (match) {
        const parsed = parseInt(match[1]);
        return Number.isNaN(parsed) ? NaN : parsed;
      }
      const fallback = parseInt(str);
      return Number.isNaN(fallback) ? NaN : fallback;
    };

    /* eslint-disable no-constant-condition */
    while (true) {
      const { data: page, error } = await supabase
        .from('documents')
        .select('number')
        .eq('type', type)
        .range(offset, offset + PAGE_SIZE - 1);
      if (error) throw error;
      for (const row of (page || [])) {
        const num = extractNumericSuffix(row.number);
        if (Number.isFinite(num) && num > lastNumber) lastNumber = num;
      }
      if (!page || page.length < PAGE_SIZE) break;
      offset += PAGE_SIZE;
    }

    // Para salidas, usar el formato "sal-000001"
    if (type === 'salida') {
      return `SAL-${(lastNumber + 1).toString().padStart(6, '0')}`;
    }

    // Para entradas, mantener el formato actual
    return (lastNumber + 1).toString().padStart(6, '0');
  } catch (error) {
    console.error('Error generating document number:', error);
    throw error;
  }
};

export const saveDocument = async (document, type) => {
  if (!document.items?.length) throw new Error('El documento debe tener al menos una línea');
  return runInventoryTransaction('save_warehouse_document', document.id || `new-${type}`, {
    p_document_id: document.id || null,
    p_document: {
      type, number: document.number, date: document.date,
      customer_id: document.customerId || null,
      entry_mode: normalizeEntryMode(document, type),
      regularization_reason: document.regularization_reason || null,
    },
    p_items: document.items.map(item => ({
      product_id: item.product_id, size: item.size, color: item.color,
      quantity: Number(item.quantity), price: Number(item.price),
      theoretical_quantity: item.theoretical_quantity == null ? null : Number(item.theoretical_quantity),
      counted_quantity: item.counted_quantity == null ? null : Number(item.counted_quantity),
    })),
    p_expected_updated_at: document.updated_at || null,
  });
};

export async function fetchDocuments(type) {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) throw new Error('No hay sesión activa');

    // Obtener el rol del usuario
    const { data: userData, error: userError } = await supabase
      .from('users')
      .select('role')
      .eq('id', session.user.id)
      .single();

    if (userError) throw userError;

    // OPTIMIZACIÓN: Consultas diferentes según el tipo de documento
    const buildQuery = () => {
      let query;
    
      if (type === 'entrada') {
        // Referencias y cantidades: filtro por artículo y total de prendas sin consultas adicionales.
        query = supabase
          .from('documents')
          .select(`
            id,
            number,
            date,
            type,
            status,
            total,
            created_at,
            updated_at,
            user_id,
            entry_mode,
            regularization_reason,
            document_items(product_id, quantity)
          `)
          .eq('type', type)
          .order('date', { ascending: false }).order('id');
      } else {
        // Para salidas: consulta optimizada con items y productos (sin imágenes para la lista)
        query = supabase
          .from('documents')
          .select(`
            id,
            number,
            date,
            type,
            status,
            total,
            created_at,
            updated_at,
            user_id,
            entry_mode,
            regularization_reason,
            customer:customers(
              id,
              name
            ),
            document_items(
              id,
              product_id,
              size,
              color,
              quantity,
              price,
              product:products(
                id,
                code,
                description
              )
            )
          `)
          .eq('type', type)
          .order('date', { ascending: false }).order('id');
      }

      // Si el usuario es vendedor, solo mostrar sus documentos
      if (userData.role === 'vendedor') {
        query = query.eq('user_id', session.user.id);
      }

      return query;
    };
    const data = await fetchAllRows(buildQuery);

    // Transformar los datos según el tipo
    const transformedData = data.map(doc => {
      const totalQuantity = (doc.document_items || []).reduce((sum, item) => sum + (Number(item.quantity) || 0), 0);
      if (type === 'entrada') {
        // Para entradas: estructura simple
        return {
          ...doc,
          totalQuantity,
          entry_mode: doc.entry_mode || 'normal',
          customerId: doc.customer?.id,
          customer: doc.customer,
          items: doc.document_items || [] // Referencias para el filtro; el detalle completo se carga al abrir
        };
      } else {
        // Para salidas: estructura completa
        return {
          ...doc,
          totalQuantity,
          entry_mode: doc.entry_mode || null,
          customerId: doc.customer?.id,
          customer: doc.customer,
          items: doc.document_items.map(item => ({
            ...item,
            product: item.product
          }))
        };
      }
    });

    return transformedData;
  } catch (error) {
    console.error('Error fetching documents:', error);
    throw error;
  }
}

export async function deleteDocument(documentId) {
  return runInventoryTransaction('delete_warehouse_document', documentId, { p_document_id: documentId });
}
